import { CalendarIcon } from "@heroicons/react/24/outline";
import { type CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { FilterChipSelect } from "@packages/core-client/collection/FilterChipSelect.tsx";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useEffect, useState } from "react";
import type { Key } from "react-aria-components";
import type { RangeValue } from "react-aria-components/DateRangePicker";
import { useTranslation } from "react-i18next";
import { DateRangePicker, DateRangePickerTrigger } from "@/components/ui/date-range-picker.tsx";
import { FieldError } from "@/components/ui/field.tsx";
import { SelectItem } from "@/components/ui/select.tsx";
import { CUSTOM_FEEDBACK_PERIOD, DEFAULT_FEEDBACK_TIME_FRAME, exceedsFeedbackMaxRange, FEEDBACK_TIME_FRAMES, type FeedbackPeriod, isSelectableFeedbackRange } from "../hooks/feedbackPeriod.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

const RANGE_SETTLE_MS = 400;

const PERIOD_ICON = <CalendarIcon aria-hidden="true" className="size-4" />;

type FeedbackDateRange = RangeValue<CalendarDate>;

export interface FeedbackPeriodToolbarProps {
    period: FeedbackPeriod;
    onPeriodChange: (period: FeedbackPeriod) => void;
    dataFloor: CalendarDate | null;
}

export function FeedbackPeriodToolbar({ period, onPeriodChange, dataFloor }: FeedbackPeriodToolbarProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("FeedbackPeriodToolbar", { i18n: i18nextInstance });

    const [isCustomPicked, setIsCustomPicked] = useState(false);
    const isCustom = isCustomPicked || period.kind === "custom";
    const selectedKey = isCustom ? CUSTOM_FEEDBACK_PERIOD : period.timeFrame;

    const [draftRange, setDraftRange] = useState<FeedbackDateRange | null>(null);
    const storedRange = period.kind === "custom" ? { start: period.start, end: period.end } : null;
    const settledRange = useDebouncedValue(draftRange, RANGE_SETTLE_MS);
    const maxDate = today(getLocalTimeZone());

    const handleSelectionChange = useCallback(
        (key: Key) => {
            setDraftRange(null);

            if (key === CUSTOM_FEEDBACK_PERIOD) {
                setIsCustomPicked(true);

                return;
            }

            setIsCustomPicked(false);
            onPeriodChange({ kind: "preset", timeFrame: FEEDBACK_TIME_FRAMES.find(value => value === key) ?? DEFAULT_FEEDBACK_TIME_FRAME });
        },
        [onPeriodChange]
    );

    const handleRangeChange = useCallback((value: FeedbackDateRange | null) => setDraftRange(value), []);

    useEffect(() => {
        if (!settledRange || settledRange !== draftRange) {
            return;
        }

        const { start, end } = settledRange;

        if (!isSelectableFeedbackRange(start, end, { floor: dataFloor, today: today(getLocalTimeZone()) })) {
            return;
        }

        if (period.kind === "custom" && start.compare(period.start) === 0 && end.compare(period.end) === 0) {
            return;
        }

        onPeriodChange({ kind: "custom", start, end });
    }, [settledRange, draftRange, period, dataFloor, onPeriodChange]);

    const validateRange = useCallback(
        (value: FeedbackDateRange | null) => {
            if (!value) {
                return null;
            }

            if (value.end.compare(value.start) < 0) {
                return t("invalidRange");
            }

            return exceedsFeedbackMaxRange(value.start, value.end) ? t("rangeTooLong") : null;
        },
        [t]
    );

    return (
        <div className="flex flex-wrap items-center gap-2">
            <FilterChipSelect ariaLabel={t("ariaLabel")} clearAriaLabel={t("clearAriaLabel")} selectedKey={selectedKey} defaultKey={DEFAULT_FEEDBACK_TIME_FRAME} onSelectionChange={handleSelectionChange} prefix={PERIOD_ICON}>
                {FEEDBACK_TIME_FRAMES.map(value => (
                    <SelectItem key={value} id={value} textValue={t(`timeFrames.${value}`)}>
                        {t(`timeFrames.${value}`)}
                    </SelectItem>
                ))}
                <SelectItem id={CUSTOM_FEEDBACK_PERIOD} textValue={t("custom")}>
                    {t("custom")}
                </SelectItem>
            </FilterChipSelect>
            {isCustom && (
                <DateRangePicker aria-label={t("rangeLabel")} value={draftRange ?? storedRange} onChange={handleRangeChange} minValue={dataFloor} maxValue={maxDate} defaultOpen={period.kind !== "custom"} validate={validateRange} validationBehavior="aria" className="w-auto">
                    <DateRangePickerTrigger />
                    <FieldError />
                </DateRangePicker>
            )}
        </div>
    );
}
